import { useState, useEffect, useCallback } from "react";
import { api } from "../api";
import { today, formatDate } from "../constants";
import Card from "./ui/Card";
import LoadingSpinner from "./ui/LoadingSpinner";
import EmptyState from "./ui/EmptyState";
import Select from "./ui/Select";

export default function AttendanceReport({ reviewers }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState(today().slice(0, 7) + "-01");
  const [dateTo, setDateTo] = useState(today());
  const [fR, setFR] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    const p = new URLSearchParams({
      date_from: dateFrom,
      date_to: dateTo,
    });
    if (fR) p.set("reviewer_id", fR);
    try {
      const r = await api(`/reports/attendance?${p}`);
      setRecords(r || []);
    } catch {}
    setLoading(false);
  }, [dateFrom, dateTo, fR]);

  useEffect(() => {
    load();
  }, [load]);

  const calcDuration = (i, o) => {
    if (!i || !o) return "—";
    const [h1, m1] = i.split(":").map(Number);
    const [h2, m2] = o.split(":").map(Number);
    let mins = h2 * 60 + m2 - (h1 * 60 + m1);
    if (mins <= 0) mins += 24 * 60; // shift spans midnight
    if (mins <= 0) return "—";
    return `${Math.floor(mins / 60)}س ${mins % 60}د`;
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
          flexWrap: "wrap",
          gap: 10,
        }}
      >
        <div>
          <h2
            style={{
              fontSize: 20,
              fontWeight: 800,
              margin: 0,
              color: "#f1f5f9",
            }}
          >
            🕐 تقرير دوام مشرفي المحطات
          </h2>
          <p style={{ color: "#64748b", fontSize: 12, margin: "4px 0 0" }}>
            البيانات مُدخلة من قبل المراجعين — تُعرض بالمحطة
          </p>
        </div>
        <span style={{ color: "#64748b", fontSize: 13 }}>
          {records.length} سجل
        </span>
      </div>
      <Card>
        <div
          style={{
            display: "flex",
            gap: 8,
            marginBottom: 14,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
            <label style={{ color: "#64748b", fontSize: 12 }}>من</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              style={{
                background: "#0f172a",
                border: "1px solid #334155",
                borderRadius: 8,
                padding: "7px 10px",
                color: "#f1f5f9",
                fontSize: 12,
                fontFamily: "inherit",
              }}
            />
          </div>
          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
            <label style={{ color: "#64748b", fontSize: 12 }}>إلى</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              style={{
                background: "#0f172a",
                border: "1px solid #334155",
                borderRadius: 8,
                padding: "7px 10px",
                color: "#f1f5f9",
                fontSize: 12,
                fontFamily: "inherit",
              }}
            />
          </div>
          <div style={{ minWidth: 180 }}>
            <Select
              value={fR}
              onChange={setFR}
              placeholder="كل المراجعين"
              options={reviewers.map((r) => ({ value: r.id, label: r.name }))}
            />
          </div>
        </div>
        {loading ? (
          <LoadingSpinner />
        ) : records.length === 0 ? (
          <EmptyState message="لا توجد سجلات" />
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 12,
              }}
            >
              <thead>
                <tr style={{ background: "#0f172a" }}>
                  {[
                    "التاريخ",
                    "المحطة",
                    "المنطقة",
                    "حضور",
                    "انصراف",
                    "المدة",
                    "مُسجَّل بواسطة",
                    "ملاحظات",
                  ].map((h) => (
                    <th
                      key={h}
                      style={{
                        padding: "9px 10px",
                        color: "#64748b",
                        textAlign: "right",
                        borderBottom: "1px solid #1e293b",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {records.map((r, i) => (
                  <tr
                    key={i}
                    style={{
                      borderBottom: "1px solid #0f172a",
                      background: i % 2 ? "#ffffff03" : "transparent",
                    }}
                  >
                    <td
                      style={{
                        padding: "8px 10px",
                        color: "#94a3b8",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {formatDate(r.entry_date)}
                    </td>
                    <td
                      style={{
                        padding: "8px 10px",
                        color: "#f1f5f9",
                        fontWeight: 700,
                        fontSize: 13,
                      }}
                    >
                      {r.station_name}
                    </td>
                    <td style={{ padding: "8px 10px", color: "#64748b" }}>
                      {r.region}
                    </td>
                    <td
                      style={{
                        padding: "8px 10px",
                        color: "#22c55e",
                        fontWeight: 800,
                        fontSize: 15,
                      }}
                    >
                      {r.check_in || "—"}
                    </td>
                    <td
                      style={{
                        padding: "8px 10px",
                        color: "#ef4444",
                        fontWeight: 800,
                        fontSize: 15,
                      }}
                    >
                      {r.check_out || "—"}
                    </td>
                    <td style={{ padding: "8px 10px" }}>
                      <span
                        style={{
                          background: "#0f2744",
                          color: "#38bdf8",
                          borderRadius: 6,
                          padding: "3px 8px",
                          fontWeight: 700,
                          fontSize: 13,
                        }}
                      >
                        {calcDuration(r.check_in, r.check_out)}
                      </span>
                    </td>
                    <td
                      style={{
                        padding: "8px 10px",
                        color: "#64748b",
                        fontSize: 11,
                      }}
                    >
                      {r.reviewer_name?.split(" ").slice(0, 2).join(" ")}
                    </td>
                    <td
                      style={{
                        padding: "8px 10px",
                        color: "#64748b",
                        fontSize: 11,
                      }}
                    >
                      {r.notes || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
